import blobSvg from "./common/blob-svg.js";
import { darkMode, reducedMotion } from "./common/dark.js";
import { nthSunflowerColour, isDark } from "./common/colours.js";
import loadPuzzle, { onStart, onWin, onCheat, puzzleId, isTodaysPuzzle } from "./common/daily.js";
import { updateStory, ADD_CORRECT, REMOVE_CORRECT, ADD_WRONG, REMOVE_WRONG, CHEAT } from "./story.js";
import "./util/layout.js";
import { openModal, closeModal, toggleModal, clearModals } from "./util/modal.js";
import $ from "./util/dom.js";
import getJson from "./util/json.js";
import storage from "./common/data.js";
import useGridKeys from "./common/grid-keys.js";
import Drag from "./common/drag.js";
import { englishList, englishPlural } from "./util/english.js";
import makeHintOffer from "./common/hint-offer.js";

const grid = [],
	els = [],
	allCells = [],
	regions = [],
	width = 7, height = 12,
	minSize = 4, maxSize = 8;
let story = [],
	guessRegions = [],
	selection = new Set(),
	won = false,
	cheated = false;

const hintOffer = makeHintOffer($.hintOffer);

let realWords;
getJson("assets/words.json").then(w => {
	realWords = new Set(w.map(w => w.toUpperCase()));
	guessRegions = guessRegions.filter(({ set }) => realWords.has(word(set, allCells)));
});

loadPuzzle().then((props) => {
	regions.push(...props.regions);
	if (props.width != width || props.height != height)
		throw new Error("Wrong puzzle dimensions");
	if (props.minSize != minSize || props.maxSize != maxSize)
		throw new Error("Wrong puzzle parameters");

	for (let y = 0; y < height; ++y) grid[y] = [];
	regions.forEach((region, i) => {
		if (region.length != props.words[i].length)
			throw new Error("Length mismatch at " + i);
		region.forEach(([x, y], j) => {
			grid[y][x] = props.words[i][j];
		});
	});

	const drag = new CellDrag();
	grid.forEach((row, y) => {
		els[y] = [];
		row.forEach((letter, x) => {
			const button = document.createElement('button');
			$.grid.appendChild(button);
			button.appendChild(document.createTextNode(letter.toUpperCase()));
			const packet = { el: button, x, y, letter };
			button.addEventListener('click', () => onClick(packet));
			drag.registerCell(button, packet);
			els[y][x] = packet;
			allCells.push(packet);
		});
	});
	useGridKeys($.grid, els, packet => new CellDrag().start(packet, {}));
	$.grid.addEventListener('keydown', e => {
		if (e.key !== 'Enter') return;
		e.preventDefault();
		if ($.addRegion.disabled) return;
		addRegion();
	});

	reloadState();
})
.catch(e => {
	console.error(e);
	alert(e.message);
});

$.addRegion.addEventListener('click', e => {
	if (won) openModal('result');
	else addRegion();
});
function addRegion() {
	hintOffer.reset();
	const s = [ ...selection ].map(({x, y}) => `${x},${y}`);
	const correct = regions.some(region =>
		region.length == s.length &&
		region.every(x => s.includes(x.join(','))));
	guessRegions.push({ set: selection, i: unusedI(), correct });
	story.push(correct ? ADD_CORRECT : ADD_WRONG);
	selection = new Set();
	redraw();
}

$.menuButton.addEventListener('click', e => toggleModal('menu'));
$.closeMenu.addEventListener('click', e => closeModal());
$.closeResult.addEventListener('click', e => closeModal());
$.revealAnswer.addEventListener('click', e => openModal('confirm-reveal'));
$.cancelReveal.addEventListener('click', e => closeModal());
$.getHint.addEventListener('click', e => openModal('confirm-hint'));
$.hintOffer.addEventListener('click', e => openModal('confirm-hint'));
$.cancelHint.addEventListener('click', e => closeModal());
$.revealStoryExplanation.addEventListener('click', e => openModal('story-explanation'));
$.closeStoryExplanation.addEventListener('click', e => closeModal(''));
$.showChangelog.addEventListener('click', e => openModal('changelog'));
$.closeChangelog.addEventListener('click', e => closeModal());
$.showInstructions.addEventListener('click', e => {
	closeModal();
	openModal('instructions');
});
$.unselect.addEventListener('click', e => {
	selection = new Set();
	redraw();
});

darkMode.addEventListener('change', redraw);
reducedMotion.addEventListener('change', redraw);

$.closeInstructions.focus();
$.closeInstructions.addEventListener('click', e => {
	closeModal();
	onStart();
	redraw();
	hintOffer.reset();
	$.revealAnswer.classList.remove('hidden');
	$.getHint.classList.remove('hidden');
});

$.okReveal.addEventListener('click', e => {
	onCheat();
	hintOffer.stop();
	cheated = true;
	for (const { el } of allCells) el.disabled = true;
	$.addRegion.disabled = true;
	guessRegions = regions.map((region, i) => ({
		set: new Set(region.map(([x, y]) => els[y][x])),
		i,
		correct: true
	}));
	selection = new Set();
	redraw();
	clearModals();
});

$.okHint.addEventListener('click', e => {
	onCheat();
	hintOffer.reset();
	story.push(CHEAT);
	clearModals();
	const wrongGuesses = guessRegions
		.filter(region => !region.correct)
		.map(region => word(region.set, allCells));
	if (wrongGuesses.length) {
		alert(`${englishList(wrongGuesses)} ${englishPlural(wrongGuesses, 'is', 'are')} wrong and will be removed`);
		guessRegions = guessRegions.filter(region => region.correct);
		redraw();
		return;
	} 
	while (true) {
		const region = regions[~~(Math.random() * regions.length)];
		const cell = els[region[0][1]][region[0][0]];
		if (guessRegions.some(g => g.set.has(cell))) continue;
		selection = new Set(region.map(([x, y]) => els[y][x]));
		redraw();
		return;
	}
});

class CellDrag extends Drag {
	start(cell, event) {
		if (won || cheated) return;
		for (const region of guessRegions)
			if (region.set.has(cell))
				return false;
		if (selection.has(cell)) {
			selection.delete(cell);
			event.add = false;
		} else {
			selection.add(cell);
			event.add = true;
		}
		redraw();
		return true;
	}
	move(cell, event) {
		if (won || cheated) return;
		for (const region of guessRegions)
			if (region.set.has(cell))
				return false;
		if (event.add) selection.add(cell);
		else selection.delete(cell);
		redraw();
	}
}

function onClick(cell) {
	if (won || cheated) return;
	for (const region of guessRegions) {
		if (region.set.has(cell)) {
			openModal('modal');
			document.getElementById('word-to-remove').innerHTML = word(region.set, allCells);
			function onSelect() {
				for (const cell of region.set) selection.add(cell);
				onOk();
			}
			function onOk() {
				story.push(region.correct ? REMOVE_CORRECT : REMOVE_WRONG);
				guessRegions = guessRegions.filter(x => x != region);
				redraw();
				clearModal();
				hintOffer.reset();
			}
			function clearModal() {
				closeModal();
				$.removeWord.removeEventListener('click', onOk);
				$.selectWord.removeEventListener('click', onSelect);
				$.closeModal.removeEventListener('click', clearModal);
				cell.el.focus();
			}
			$.removeWord.addEventListener('click', onOk, { once: true });
			$.selectWord.addEventListener('click', onSelect, { once: true });
			$.closeModal.addEventListener('click', clearModal, { once: true });
			$.removeWord.focus();
			return;
		}
	}
}

function redraw() {
	saveState();
	for (const cell of allCells) {
		let got = false;
		if (selection.has(cell)) {
			got = true;
			colourCell(cell, selection, 0);
		}
		else for (const region of guessRegions)
			if (region.set.has(cell)) {
				got = true;
				colourCell(cell, region.set, region.i + 1);
			}
		if (!got) colourCell(cell, new Set([ cell ]), false);
	}
	const currentGuess = word(selection, allCells);
	$.addRegion.disabled = true;
	$.unselect.classList.add('hidden');
	if (cheated)
		$.guessSoFar.innerHTML = `Solution revealed`;
	else if (guessRegions.reduce((a, n) => a + n.set.size, 0) == allCells.length)
		handleVictory();
	else if (selection.size == 0)
		$.guessSoFar.innerHTML = `Enter a word`;
	else {
		$.unselect.classList.remove('hidden');
		if (!contiguous(selection))
			$.guessSoFar.innerHTML = `Regions must link up`;
		else if (selection.size < minSize)
			$.guessSoFar.innerHTML = `Too short: ${currentGuess}`;
		else if (selection.size > maxSize)
			$.guessSoFar.innerHTML = `Words must be 4-8 letters`;
		else if (realWords && !realWords.has(currentGuess))
			$.guessSoFar.innerHTML = `Not a word: ${currentGuess}`;
		else {
			$.addRegion.disabled = false;
			$.guessSoFar.innerHTML = `Tap to add ${currentGuess}`;
		}
	}
}

function word(set, allCells) {
	return allCells
		.filter(x => set.has(x))
		.map(x => x.letter.toUpperCase())
		.join('');
}

function unusedI() {
	for (let i = 0; ; ++i)
		if (!guessRegions.some(r => r.i == i))
			return i;
}

function neighbours(cell, region) {
	for (const {x, y} of region)
		if (Math.abs(x - cell.x) + Math.abs(y - cell.y) < 2)
			return true;
	return false;
}

function contiguous(region) {
	let candidates = [...region];
	const contiguous = new Set([candidates.pop()]);
	while (true) {
		const nope = [];
		for (const cell of candidates)
			if (neighbours(cell, contiguous)) contiguous.add(cell);
			else nope.push(cell);
		if (nope.length == 0) return true;
		if (nope.length == candidates.length) return false;
		candidates = nope;
	}
}

function doFrame() {
	for (const cell of allCells)
		cell.el.style.opacity = (selection.has(cell) && !reducedMotion.active)
			? Math.sin(Date.now() * 0.004) * 0.3 + 0.7
			: 1;
	window.requestAnimationFrame(doFrame);
}
window.requestAnimationFrame(doFrame);

function getBlobUrl(cell, set, n) {
	if (n === false) return blobSvg(isInSet, cell, '#bbbbbb');
	if (n) return blobSvg(isInSet, cell, nthSunflowerColour(n));
	if (reducedMotion.active) return blobSvg(
		isInSet, cell,
		nthSunflowerColour(0, 100, darkMode.active ? 25 : 85),
		nthSunflowerColour(0, 100, 50)
	);
	return blobSvg(isInSet, cell, nthSunflowerColour(0, 100));
	function isInSet(x, y) {
		return set.has(els[y]?.[x]);
	}
}

function colourCell(cell, set, n) {
	const i = new Image();
	const { url, colour } = getBlobUrl(cell, set, n);
	i.addEventListener('error', e => console.error(e.message, e));
	i.addEventListener('load', () => {
		cell.el.style.backgroundImage = url;
		cell.el.style.color = isDark(colour) ? '#fff' : '#000';
	}, { once: true });
	i.src = url.substring(5, url.length - 2);
}

function handleVictory() {
	hintOffer.stop();
	for (const { el } of allCells) el.disabled = true;
	$.guessSoFar.innerHTML = "Show result";
	$.addRegion.disabled = false;
	$.getHint.classList.add('hidden');
	$.revealAnswer.classList.add('hidden');
	updateStory(story);
	if (!won) {
		won = true;
		openModal('result');
		onWin();
	}
}

let unsavedListenerRegistered = false;
function saveState() {
	if (!isTodaysPuzzle) {
		if (!unsavedListenerRegistered) {
			unsavedListenerRegistered = true;
			// Only register this listener when it's needed — registering it has a performance hit which we should avoid if we can.
			window.addEventListener('beforeunload', e => {
				if (guessRegions.length == 0 || won || cheated) return;
				// preventDefault is the correct way to prompt the user if they're sure:
				e.preventDefault();
				// returning a string and setting returnValue are deprecated but harmless and some browsers don't support the new way yet so let's belt and braces this
				return event.returnValue = "If you leave this page your game will not be saved.";
			}, { capture: true });
		}
		return;
	}
	storage.savedState = {
		puzzleId,
		regions: guessRegions.map(r => ({
			set: [...r.set].map(el => [el.x, el.y]),
			i: r.i,
			correct: r.correct
		})),
		selection: [...selection].map(el => [el.x, el.y]),
		story, won, cheated,
	};
}

function reloadState() {
	if (storage.savedState.puzzleId != puzzleId)
		return;
	guessRegions = storage.savedState.regions.map(r => ({
		set: new Set(r.set.map(([x, y]) => els[y][x])),
		i: r.i,
		correct: r.correct
	}));
	selection = new Set(storage.savedState.selection.map(([x, y]) => els[y][x]))
	story = storage.savedState.story;
	won = storage.savedState.won;
	cheated = storage.savedState.cheated;
	closeModal();
	$.revealAnswer.classList.remove('hidden');
	$.getHint.classList.remove('hidden');
	hintOffer.reset();
	if (won) handleVictory();
	redraw();
}
